import { Injectable, Inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Registration } from '../model/registration';
import { RegistrationServiceBase } from '../../../_common/services/registration.service.base';
import { API_URL, BUSINESS } from '../../../_common/app/app.config';
import { CommunityService } from '../../../_common/services/community.service';
import { Observable, combineLatest, of, from, throwError } from 'rxjs';
import { catchError, flatMap, map, switchMap, take } from 'rxjs/operators';
import { AuthService } from '@common/services/auth.service';
import { Router } from '@angular/router';

@Injectable()
export class RegistrationService extends RegistrationServiceBase<Registration> {
    homePageSelectors: { prefecture: string; city: string };

    constructor(
        @Inject(BUSINESS) business,
        http: HttpClient,
        @Inject(API_URL) baseUrl: string,
        communityService: CommunityService,
        auth: AuthService,
        private router: Router,
    ) {
        super(business, http, baseUrl, communityService, auth);
        this.participantMinimum = 1000;
    }

    public get enableRegistrationFlow$(): Observable<boolean> {
        const result$ = combineLatest([
            this.communityService.community$,
            this.communityService.currentAuctionOpenForNewRegistrations$,
        ]);

        return result$.pipe(
            map(([community, currentAuctionOpenForNewRegistrations]) => {
                return (
                    !community.preventInterestedIndividuals ||
                    currentAuctionOpenForNewRegistrations
                );
            }),
        );
    }

    getBills(id: string): Observable<any> {
        return this.http.get<any>(`${this.url}/${id}/bills`);
    }

    removeElectricityBill(id: string, fileName: string): Observable<any> {
        return this.http.delete<any>(
            `${this.url}/${id}/electricitybill/${fileName}`,
        );
    }

    removeGasBill(id: string, fileName: string): Observable<any> {
        return this.http.delete<any>(`${this.url}/${id}/gasbill/${fileName}`);
    }

    revertDecision(id: string): Observable<void> {
        return this.http.post<void>(
            this.url + '/' + id + '/revert-decision-as-user',
            null,
        );
    }

    persistRegistration(input: {
        registration: Registration;
        communityCode: string;
    }): void {
        sessionStorage.setItem(
            `registration-${this.business}-${input.communityCode}`,
            JSON.stringify(input.registration),
        );
    }

    add(registration: Registration): Observable<string> {
        return this.http
            .post<string>(this.url, registration, { observe: 'response' })
            .pipe(
                switchMap((response) => {
                    const registrationId = response.body;
                    return this.auth
                        .loginAsParticipant(response)
                        .pipe(switchMap((_) => of(registrationId)));
                }),
            );
    }

    get(id: string): Observable<Registration> {
        return this.http.get<Registration>(`${this.url}/${id}`).pipe(
            catchError((e) => {
                const unauthorizedStatuses = [401, 403, 404];
                if (
                    unauthorizedStatuses.find((status) => status === e?.status)
                ) {
                    return this.communityService.communityCode$.pipe(
                        take(1),
                        flatMap((communityCode) =>
                            from(
                                this.router.navigate([
                                    communityCode,
                                    'unauthorized',
                                    id,
                                ]),
                            ),
                        ),
                        map(() => new Registration()),
                    );
                }
                return throwError(e);
            }),
        );
    }

    sendLinkToRegistration(registrationId: string): Observable<void> {
        const url = `${this.url}/${registrationId}/send-participant-link`;
        return this.http.post<void>(url, {});
    }
}
